import { get } from 'lodash'
import { groq } from 'next-sanity'
import ErrorPage from 'next/error'
import { useRouter } from 'next/router'
import T from 'prop-types'
import React from 'react'

import { lessonQuery } from '../../api'
import { getImageUrl } from '../../api/sanity'
import Content from '../../components/Content'
import LessonCover from '../../components/LessonCover'
import LessonEntry from '../../components/LessonEntry'
import NavList from '../../components/NavList'
import SanityPreview from '../../components/SanityPreview'
import SEO from '../../components/SEO'
import useColor from '../../hooks/useColor'
import useFocus from '../../hooks/useFocus'
// import getType from '../../lib/types'
import CompetencyGoals from '../../components/CompetencyGoals'
import getSectionKeys from '../../helpers/get-section-keys'
import { buildGetStaticPaths, buildGetStaticProps } from '../../lib/page'
import getTopic from '../../lib/topic'

const query = groq`
  *[_type == "lesson" && slug.current == $slug]${lessonQuery()} | order(_updatedAt desc)[0]
`

function InnerUndervisningsoppleggPage({ lesson, status }) {
  useColor(lesson, status)
  // const { illustration } = getType(lesson?.type)
  const { illustration } = getTopic(lesson?.topicTheme)

  const competencyGoals = lesson?.competencyGoals || []
  const isIntro = lesson?.types?.intro
  const sections = get(lesson, 'sections', []) || []
  const navigation = [
    ...sections,
    ...(competencyGoals.length > 0 ? [{ _key: 'compentency-goals', title: 'Kompetansemål' }] : []),
  ]

  const active = useFocus([
    ...getSectionKeys(sections),
    ...(competencyGoals?.length > 0 ? ['compentency-goals'] : []),
  ])

  const lessons = get(lesson, 'topic.lessons', [])
  return (
    <div>
      <SEO
        title={lesson?.title}
        description={lesson?.purpose}
        path={`undervisningsopplegg/${lesson?.slug}`}
        image={lesson?.ogImages?.facebook && getImageUrl(lesson?.ogImages?.facebook).url()}
        excludeFromSearch={lesson?.excludeFromSearch}
      />

      <NavList
        items={navigation}
        active={active?.activeMainSection}
        inline
        back={
          lesson?.topic
            ? { title: lesson?.topic.title, href: `/tema/${lesson?.topic?.slug}` }
            : undefined
        }
      />

      <LessonCover
        {...lesson}
        muted={active?.activeSection !== 'cover'}
        illustration={illustration}
        collaborators={lesson?.collaborators}
      />

      <Content data={sections} active={active} />

      {competencyGoals.length > 0 && <CompetencyGoals data={competencyGoals} />}

      {/* TODO: Remember to update this when we get illustrations */}
      {isIntro && (
        <LessonEntry
          id="lessons"
          title={`Mer fra tema ${lesson?.topic?.title}`}
          items={lessons.map((item) => ({
            ...item,
            topic: { slug: lesson?.topic?.slug },
            topicTheme: lesson?.topicTheme,
          }))}
        />
      )}
    </div>
  )
}

export default function Page({ preview, pageData, error, status, slug }) {
  const router = useRouter()

  if (!router.isFallback && !pageData) {
    return <ErrorPage statusCode={404} />
  }

  if (error) {
    return <ErrorPage statusCode={error.statusCode} title={error.title} />
  }

  return (
    <SanityPreview preview={preview} initialData={pageData} query={query} params={{ slug }}>
      {(lesson) => {
        return <InnerUndervisningsoppleggPage lesson={lesson} status={status} />
      }}
    </SanityPreview>
  )
}

Page.propTypes = {
  status: T.string,
  pageData: T.shape({
    lesson: T.shape({
      slug: T.string,
      title: T.string,
      purpose: T.string,
      topic: T.object,
      sections: T.arrayOf(
        T.shape({
          _key: T.string.isRequired,
          _type: T.string.isRequired,
          title: T.string,
          assets: T.arrayOf(T.object),
          extra: T.arrayOf(T.object),
          content: T.shape({
            _key: T.string.isRequired,
            blocks: T.arrayOf(T.object),
          }),
        }),
      ),
    }),
  }).isRequired,
  slug: T.string.isRequired,
  preview: T.bool.isRequired,
  error: T.shape({
    statusCode: T.number.isRequired,
    title: T.string.isRequired,
    purpose: T.string,
  }),
}

export const getStaticProps = buildGetStaticProps(query)

export const getStaticPaths = buildGetStaticPaths('lesson')
